import { Injectable } from '@angular/core'
import { addDoc, collection, collectionData, doc, Firestore, getDocs, orderBy, query, serverTimestamp, updateDoc, deleteDoc } from '@angular/fire/firestore'
import { Comment, adminClientCommentsPath, FirestoreStructure } from '@parkupp/core'
import * as firestore from 'firebase/firestore'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { AuthenticationService } from './authentication.service'

@Injectable({
    providedIn: 'root',
})
export class CommentService {
    constructor(private firestore: Firestore, private authService: AuthenticationService) {}

    /**
     * Get collection reference for comments subcollection
     */
    private getCommentsCollection(adminClientKey: string) {
        return collection(this.firestore, adminClientCommentsPath(adminClientKey))
    }

    /**
     * List all comments for an admin client as an observable (real-time updates)
     */
    listRealtime(adminClientKey: string): Observable<Comment[]> {
        const commentsQuery = query(this.getCommentsCollection(adminClientKey), orderBy('createdAt', 'asc'))
        return collectionData(commentsQuery, { idField: '$key' }).pipe(map((docs) => docs.map((doc) => new Comment(doc as Partial<Comment>, doc['$key'] as string))))
    }

    /**
     * List all comments for an admin client (one-time fetch)
     */
    async list(adminClientKey: string): Promise<Comment[]> {
        const commentsQuery = query(this.getCommentsCollection(adminClientKey), orderBy('createdAt', 'asc'))
        const snapshot = await getDocs(commentsQuery)
        return snapshot.docs.map((doc) => new Comment({ ...doc.data(), $key: doc.id }))
    }

    /**
     * Add a new comment to an admin client
     */
    async add(adminClientKey: string, text: string): Promise<Comment> {
        const user = this.authService.user
        if (!user) {
            throw new Error('User must be authenticated to add comments')
        }

        const comment = new Comment({
            text,
            createdBy: user.email || 'Unknown',
            createdByName: user.displayName || user.email || 'Unknown User',
            createdAt: serverTimestamp() as firestore.Timestamp,
        })

        const commentsCollection = this.getCommentsCollection(adminClientKey)
        const docRef = await addDoc(commentsCollection, comment.parse())
        comment.$key = docRef.id

        // Update the admin client with latest comment info
        await this.updateAdminClientCommentMetadata(adminClientKey)

        return comment
    }

    /**
     * Add a system comment (no authentication required)
     * Used for automated comments from forms, etc.
     * Note: Does not update metadata since anonymous users can't read/update AdminClient
     */
    async addSystemComment(adminClientKey: string, text: string, createdBy: string = 'system@parkupp.co.za', createdByName: string = 'System'): Promise<Comment> {
        const comment = new Comment({
            text,
            createdBy,
            createdByName,
            createdAt: serverTimestamp() as firestore.Timestamp,
        })

        const commentsCollection = this.getCommentsCollection(adminClientKey)
        const docRef = await addDoc(commentsCollection, comment.parse())
        comment.$key = docRef.id

        // Note: We don't call updateAdminClientCommentMetadata here because
        // anonymous users don't have permission to read comments or update AdminClient.
        // The metadata will be updated when an admin views the lead.

        return comment
    }

    /**
     * Delete a comment
     */
    async delete(adminClientKey: string, commentKey: string): Promise<void> {
        const commentRef = doc(this.firestore, `${adminClientCommentsPath(adminClientKey)}/${commentKey}`)
        await deleteDoc(commentRef)

        // Update the admin client with latest comment info
        await this.updateAdminClientCommentMetadata(adminClientKey)
    }

    /**
     * Update admin client document with comments count and latest comment
     */
    private async updateAdminClientCommentMetadata(adminClientKey: string): Promise<void> {
        const comments = await this.list(adminClientKey)

        const adminClientRef = doc(this.firestore, `${FirestoreStructure.ADMIN_CLIENTS}/${adminClientKey}`)

        const latestComment = comments.length > 0 ? comments[comments.length - 1] : null

        await updateDoc(adminClientRef, {
            commentsCount: comments.length,
            latestComment: latestComment
                ? {
                      text: latestComment.text,
                      createdBy: latestComment.createdBy,
                      createdByName: latestComment.createdByName,
                      createdAt: latestComment.createdAt,
                  }
                : null,
            updatedAt: serverTimestamp(),
        })
    }
}
